import React, { useRef, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { 
  generateSectionReportPDF, 
  generatePDFFromElement 
} from "../../../utils/invoiceUtils";

const ViewParentEngagementIModal = ({ 
  isOpen, 
  onClose, 
  studentId,
  sectionId, 
  isSectionReport, 
  sectionName, 
  className,
  campusName
}) => {
  const reportRef = useRef();
  const [loading, setLoading] = useState(false);

  // Dummy data for individual parent engagement report
  const dummyIndividualReport = {
    parentName: "John Smith",
    kids: [
      { id: "STD001", name: "Emma Smith", class: "Grade 8", section: "A" },
      { id: "STD002", name: "Liam Smith", class: "Grade 5", section: "B" }
    ],
    portalAccess: {
      lastLogin: "2024-01-15T14:30:00Z",
      totalLogins: 45,
      loginFrequency: "Weekly",
      averageSessionTime: "12 minutes"
    },
    surveys: {
      totalSubmitted: 8,
      breakup: [
        { type: "Academic Satisfaction", count: 3 },
        { type: "School Facilities", count: 2 },
        { type: "Teacher Performance", count: 2 },
        { type: "General Feedback", count: 1 }
      ],
      totalAvailable: 12,
      submissionRate: "67%"
    },
    complaints: {
      totalRegistered: 2,
      resolved: 2,
      pending: 0,
      categories: [
        { type: "Academic", count: 1 },
        { type: "Administrative", count: 1 }
      ]
    },
    notifications: {
      totalSent: 156,
      checked: 142,
      checkPercentage: "91%",
      categories: [
        { type: "Academic Updates", sent: 45, checked: 42 },
        { type: "Events & Activities", sent: 38, checked: 35 },
        { type: "Fee Reminders", sent: 25, checked: 24 },
        { type: "General Announcements", sent: 48, checked: 41 }
      ]
    },
    engagementScore: 88,
    engagementLevel: "High"
  };

  // Dummy data for section report
  const dummySectionReport = [
    {
      parentName: "Alice Johnson",
      kids: [{ name: "Michael Johnson", class: "Grade 8", section: "A" }],
      portalAccess: { totalLogins: 32, lastLogin: "2024-01-14" },
      surveys: { submitted: 6, available: 12, rate: "50%" },
      complaints: { total: 1, resolved: 1 },
      notifications: { sent: 156, checked: 145, percentage: "93%" },
      engagementScore: 85
    },
    {
      parentName: "Robert Davis",
      kids: [
        { name: "Sophie Davis", class: "Grade 8", section: "A" },
        { name: "Jake Davis", class: "Grade 6", section: "B" }
      ],
      portalAccess: { totalLogins: 28, lastLogin: "2024-01-13" },
      surveys: { submitted: 4, available: 12, rate: "33%" },
      complaints: { total: 0, resolved: 0 },
      notifications: { sent: 156, checked: 124, percentage: "79%" },
      engagementScore: 72
    },
    {
      parentName: "Maria Garcia",
      kids: [{ name: "Carlos Garcia", class: "Grade 8", section: "A" }],
      portalAccess: { totalLogins: 52, lastLogin: "2024-01-15" },
      surveys: { submitted: 9, available: 12, rate: "75%" },
      complaints: { total: 3, resolved: 2 },
      notifications: { sent: 156, checked: 156, percentage: "100%" },
      engagementScore: 95
    },
    {
      parentName: "David Wilson",
      kids: [{ name: "Emma Wilson", class: "Grade 8", section: "A" }],
      portalAccess: { totalLogins: 15, lastLogin: "2024-01-10" },
      surveys: { submitted: 2, available: 12, rate: "17%" },
      complaints: { total: 0, resolved: 0 },
      notifications: { sent: 156, checked: 89, percentage: "57%" },
      engagementScore: 58
    }
  ];

  // Simulate loading for better UX
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, [isOpen]);

  const downloadPDF = () => {
    if (isSectionReport) {
      generateSectionReportPDF(
        dummySectionReport,
        className,
        sectionName,
        campusName,
        "Section_Parent_Engagement_Report.pdf"
      );
    } else {
      generatePDFFromElement(reportRef, "Parent_Engagement_Report.pdf");
    }
  };

  const downloadExcel = () => {
    if (isSectionReport) {
      const workbook = XLSX.utils.book_new();
      const summaryData = [
        ["Parent Engagement Section Report"],
        ["Campus", campusName],
        ["Class", className],
        ["Section", sectionName],
        ["Generated On", new Date().toLocaleDateString('en-GB')],
        [],
        ["Parent Name", "Number of Kids", "Portal Logins", "Last Login", "Surveys Submitted", "Survey Rate", "Complaints", "Notification Check %", "Engagement Score"],
      ];
      
      dummySectionReport.forEach(parent => {
        summaryData.push([
          parent.parentName,
          parent.kids.length,
          parent.portalAccess.totalLogins,
          new Date(parent.portalAccess.lastLogin).toLocaleDateString('en-GB'),
          `${parent.surveys.submitted}/${parent.surveys.available}`,
          parent.surveys.rate,
          parent.complaints.total,
          parent.notifications.percentage,
          parent.engagementScore
        ]);
      });
      
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
      
      XLSX.writeFile(workbook, "Section_Parent_Engagement_Report.xlsx");
    } else {
      const worksheetData = [
        ["Parent Engagement Report"],
        ["Campus", campusName],
        ["Parent Name", dummyIndividualReport.parentName],
        ["Generated On", new Date().toLocaleDateString('en-GB')],
        [],
        ["Metric", "Value"],
        ["Number of Children", dummyIndividualReport.kids.length],
        ["Total Portal Logins", dummyIndividualReport.portalAccess.totalLogins],
        ["Last Login", new Date(dummyIndividualReport.portalAccess.lastLogin).toLocaleDateString('en-GB')],
        ["Surveys Submitted", `${dummyIndividualReport.surveys.totalSubmitted}/${dummyIndividualReport.surveys.totalAvailable}`],
        ["Survey Submission Rate", dummyIndividualReport.surveys.submissionRate],
        ["Complaints Registered", dummyIndividualReport.complaints.totalRegistered],
        ["Notification Check Rate", dummyIndividualReport.notifications.checkPercentage],
        ["Engagement Score", `${dummyIndividualReport.engagementScore}/100`],
        ["Engagement Level", dummyIndividualReport.engagementLevel],
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Parent Engagement");
      XLSX.writeFile(workbook, "Parent_Engagement_Report.xlsx");
    }
  };

  const getEngagementColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getEngagementLevel = (score) => {
    if (score >= 80) return 'High';
    if (score >= 60) return 'Medium';
    return 'Low';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div ref={reportRef} className="bg-white p-6 rounded-lg shadow-lg w-[90%] max-h-[90vh] overflow-y-auto relative border border-gray-300">
        {loading && <p>Loading report...</p>}

        {isSectionReport ? (
          <>
            {/* Section Report Header */}
            <div className="text-center mb-6 border-b pb-4">
              <h1 className="text-2xl font-bold text-gray-800">{campusName}</h1>
              <h2 className="text-xl font-semibold mt-2">Parent Engagement Section Report</h2>
              <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
                <div><strong>Class:</strong> {className}</div>
                <div><strong>Section:</strong> {sectionName}</div>
                <div><strong>Generated On:</strong> {new Date().toLocaleDateString('en-GB')}</div>
              </div>
            </div>

            {/* Section Summary */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Section Summary</h3>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="bg-blue-50 p-4 rounded">
                  <div className="text-2xl font-bold text-blue-600">{dummySectionReport.length}</div>
                  <div className="text-sm text-gray-600">Total Parents</div>
                </div>
                <div className="bg-green-50 p-4 rounded">
                  <div className="text-2xl font-bold text-green-600">
                    {Math.round(dummySectionReport.reduce((sum, p) => sum + p.engagementScore, 0) / dummySectionReport.length)}
                  </div>
                  <div className="text-sm text-gray-600">Avg Engagement</div>
                </div>
                <div className="bg-purple-50 p-4 rounded">
                  <div className="text-2xl font-bold text-purple-600">
                    {Math.round(dummySectionReport.reduce((sum, p) => sum + parseInt(p.notifications.percentage), 0) / dummySectionReport.length)}%
                  </div>
                  <div className="text-sm text-gray-600">Avg Notification Check</div>
                </div>
                <div className="bg-yellow-50 p-4 rounded">
                  <div className="text-2xl font-bold text-yellow-600">
                    {Math.round(dummySectionReport.reduce((sum, p) => sum + parseInt(p.surveys.rate), 0) / dummySectionReport.length)}%
                  </div>
                  <div className="text-sm text-gray-600">Avg Survey Rate</div>
                </div>
              </div>
            </div>

            {/* Detailed Parent List */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-2 text-left">Parent Name</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Kids</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Portal Access</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Surveys</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Complaints</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Notifications</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">Engagement</th>
                  </tr>
                </thead>
                <tbody>
                  {dummySectionReport.map((parent, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2 font-medium">
                        {parent.parentName}
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <div className="space-y-1">
                          {parent.kids.map((kid, kidIndex) => (
                            <div key={kidIndex} className="text-sm">
                              {kid.name} ({kid.class})
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <div className="text-sm">
                          <div><strong>Logins:</strong> {parent.portalAccess.totalLogins}</div>
                          <div><strong>Last:</strong> {new Date(parent.portalAccess.lastLogin).toLocaleDateString('en-GB')}</div>
                        </div>
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <div className="text-sm">
                          <div>{parent.surveys.submitted}/{parent.surveys.available}</div>
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                            {parent.surveys.rate}
                          </span>
                        </div>
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-center">
                        {parent.complaints.total}
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                          {parent.notifications.percentage}
                        </span>
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <div className="text-center">
                          <div className="text-lg font-bold">{parent.engagementScore}</div>
                          <span className={`px-2 py-1 rounded text-xs ${getEngagementColor(parent.engagementScore)}`}>
                            {getEngagementLevel(parent.engagementScore)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <>
            {/* Individual Report Header */}
            <div className="text-center mb-6 border-b pb-4">
              <h1 className="text-2xl font-bold text-gray-800">{campusName}</h1>
              <h2 className="text-xl font-semibold mt-2">Parent Engagement Report</h2>
              <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                <div><strong>Parent Name:</strong> {dummyIndividualReport.parentName}</div>
                <div><strong>Generated On:</strong> {new Date().toLocaleDateString('en-GB')}</div>
              </div>
            </div>

            {/* Engagement Overview */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Engagement Overview</h3>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="bg-blue-50 p-4 rounded">
                  <div className="text-2xl font-bold text-blue-600">{dummyIndividualReport.kids.length}</div>
                  <div className="text-sm text-gray-600">Children</div>
                </div>
                <div className="bg-green-50 p-4 rounded">
                  <div className="text-2xl font-bold text-green-600">{dummyIndividualReport.portalAccess.totalLogins}</div>
                  <div className="text-sm text-gray-600">Portal Logins</div>
                </div>
                <div className="bg-purple-50 p-4 rounded">
                  <div className="text-2xl font-bold text-purple-600">{dummyIndividualReport.surveys.submissionRate}</div>
                  <div className="text-sm text-gray-600">Survey Rate</div>
                </div>
                <div className="bg-yellow-50 p-4 rounded">
                  <div className="text-2xl font-bold text-yellow-600">{dummyIndividualReport.notifications.checkPercentage}</div>
                  <div className="text-sm text-gray-600">Notification Check</div>
                </div>
              </div>
            </div>

            {/* Children Information */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Children Information</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2 text-left">Student Name</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Class</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Section</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dummyIndividualReport.kids.map((kid, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="border border-gray-300 px-4 py-2">{kid.name}</td>
                        <td className="border border-gray-300 px-4 py-2">{kid.class}</td>
                        <td className="border border-gray-300 px-4 py-2">{kid.section}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Portal Access Details */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Portal Access Log</h3>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Logins:</span>
                    <span className="font-semibold">{dummyIndividualReport.portalAccess.totalLogins}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Login Frequency:</span>
                    <span className="font-semibold">{dummyIndividualReport.portalAccess.loginFrequency}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Last Login:</span>
                    <span className="font-semibold">
                      {new Date(dummyIndividualReport.portalAccess.lastLogin).toLocaleDateString('en-GB')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Avg Session Time:</span>
                    <span className="font-semibold">{dummyIndividualReport.portalAccess.averageSessionTime}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Survey Information */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Survey Participation</h3>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-gray-600">Surveys Submitted:</span>
                    <span className="font-semibold">
                      {dummyIndividualReport.surveys.totalSubmitted}/{dummyIndividualReport.surveys.totalAvailable}
                      <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                        {dummyIndividualReport.surveys.submissionRate}
                      </span>
                    </span>
                  </div>
                  <h4 className="font-medium mb-2">Survey Breakup:</h4>
                  <div className="space-y-2">
                    {dummyIndividualReport.surveys.breakup.map((survey, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-gray-600">{survey.type}:</span>
                        <span className="font-medium">{survey.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium mb-3">Survey Performance</h4>
                  <div className="bg-gray-50 p-4 rounded">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600 mb-2">
                        {dummyIndividualReport.surveys.submissionRate}
                      </div>
                      <div className="text-sm text-gray-600">Completion Rate</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Complaints Information */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Complaints Registered</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-red-50 p-4 rounded text-center">
                  <div className="text-2xl font-bold text-red-600">{dummyIndividualReport.complaints.totalRegistered}</div>
                  <div className="text-sm text-gray-600">Total Complaints</div>
                </div>
                <div className="bg-green-50 p-4 rounded text-center">
                  <div className="text-2xl font-bold text-green-600">{dummyIndividualReport.complaints.resolved}</div>
                  <div className="text-sm text-gray-600">Resolved</div>
                </div>
                <div className="bg-yellow-50 p-4 rounded text-center">
                  <div className="text-2xl font-bold text-yellow-600">{dummyIndividualReport.complaints.pending}</div>
                  <div className="text-sm text-gray-600">Pending</div>
                </div>
              </div>
              <div className="mt-4">
                <h4 className="font-medium mb-2">Complaint Categories:</h4>
                <div className="grid grid-cols-2 gap-4">
                  {dummyIndividualReport.complaints.categories.map((category, index) => (
                    <div key={index} className="flex justify-between text-sm bg-gray-50 p-2 rounded">
                      <span className="text-gray-600">{category.type}:</span>
                      <span className="font-medium">{category.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Notification Information */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Notification Engagement</h3>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-blue-50 p-4 rounded text-center">
                  <div className="text-2xl font-bold text-blue-600">{dummyIndividualReport.notifications.totalSent}</div>
                  <div className="text-sm text-gray-600">Total Sent</div>
                </div>
                <div className="bg-green-50 p-4 rounded text-center">
                  <div className="text-2xl font-bold text-green-600">{dummyIndividualReport.notifications.checked}</div>
                  <div className="text-sm text-gray-600">Checked</div>
                </div>
                <div className="bg-purple-50 p-4 rounded text-center">
                  <div className="text-2xl font-bold text-purple-600">{dummyIndividualReport.notifications.checkPercentage}</div>
                  <div className="text-sm text-gray-600">Check Rate</div>
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-3">Notification Breakdown by Category:</h4>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border border-gray-300 px-4 py-2 text-left">Category</th>
                        <th className="border border-gray-300 px-4 py-2 text-center">Sent</th>
                        <th className="border border-gray-300 px-4 py-2 text-center">Checked</th>
                        <th className="border border-gray-300 px-4 py-2 text-center">Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dummyIndividualReport.notifications.categories.map((category, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-4 py-2">{category.type}</td>
                          <td className="border border-gray-300 px-4 py-2 text-center">{category.sent}</td>
                          <td className="border border-gray-300 px-4 py-2 text-center">{category.checked}</td>
                          <td className="border border-gray-300 px-4 py-2 text-center">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                              {Math.round((category.checked / category.sent) * 100)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Engagement Score */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Overall Engagement Score</h3>
              <div className="text-center bg-gray-50 p-6 rounded-lg">
                <div className="text-6xl font-bold mb-2" style={{color: dummyIndividualReport.engagementScore >= 80 ? '#10B981' : dummyIndividualReport.engagementScore >= 60 ? '#F59E0B' : '#EF4444'}}>
                  {dummyIndividualReport.engagementScore}
                </div>
                <div className="text-xl text-gray-600 mb-2">out of 100</div>
                <span className={`px-4 py-2 rounded-full text-lg font-medium ${getEngagementColor(dummyIndividualReport.engagementScore)}`}>
                  {dummyIndividualReport.engagementLevel} Engagement
                </span>
              </div>
            </div>
          </>
        )}

        {/* Download Buttons */}
        <div className="no-print flex justify-end mt-6 space-x-4 border-t pt-4">
          <button onClick={downloadPDF} className="bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded text-white transition-colors">
            Download as PDF
          </button>
          <button onClick={downloadExcel} className="bg-green-500 hover:bg-green-600 px-4 py-2 rounded text-white transition-colors">
            Download as Excel
          </button>
          <button onClick={onClose} className="bg-gray-400 hover:bg-gray-500 px-4 py-2 rounded text-white transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewParentEngagementIModal;